import { RelabelCaseStatus, RelabelQuantityChangeReason } from "src/types";

export const RELABEL_CASE_STATUS_LABEL: Record<RelabelCaseStatus, string> = {
  PENDING_SCAN_IN: "Case Assigned",
  IN_RELABELLING: "In Relabelling",
  COMPLETE: "Complete",
  ON_PALLET: "On Pallet",
  IN_TRANSIT: "In Transit",
  BOOKED_IN: "Booked In"
};

export const RELABEL_CASE_STATUS_STYLE: Record<RelabelCaseStatus, { pill: string; dot: string }> = {
  PENDING_SCAN_IN: { pill: "bg-amber-50 text-amber-700 border border-amber-200", dot: "bg-amber-500" },
  IN_RELABELLING: { pill: "bg-blue-50 text-blue-700 border border-blue-200", dot: "bg-blue-500" },
  COMPLETE: { pill: "bg-emerald-50 text-emerald-700 border border-emerald-200", dot: "bg-emerald-500" },
  ON_PALLET: { pill: "bg-purple-50 text-purple-700 border border-purple-200", dot: "bg-purple-500" },
  IN_TRANSIT: { pill: "bg-orange-50 text-orange-700 border border-orange-200", dot: "bg-orange-500" },
  BOOKED_IN: { pill: "bg-teal-50 text-teal-700 border border-teal-200", dot: "bg-teal-500" }
};

export const RELABEL_CASE_STATUS_VARIANT: Record<RelabelCaseStatus, "outline" | "secondary" | "success"> = {
  PENDING_SCAN_IN: "outline",
  IN_RELABELLING: "secondary",
  COMPLETE: "success",
  ON_PALLET: "success",
  IN_TRANSIT: "secondary",
  BOOKED_IN: "success"
};

// Statuses where the case has finished relabelling and an output set of relabelled
// SKUs exists on the record. Used to gate any UI that should only render once
// relabelling is done (e.g. the Output column on the tracking table).
export const RELABELLED_CASE_STATUSES: ReadonlySet<RelabelCaseStatus> = new Set<RelabelCaseStatus>([
  "COMPLETE",
  "ON_PALLET",
  "IN_TRANSIT",
  "BOOKED_IN"
]);

// Single source of truth for routing type colours used across EU Relabel Check-In,
// Case Builder, Case Tracking, and any future screens.

export const ROUTING_TYPE_CONFIG = {
  UK_RELABEL: {
    label: "UK Relabel",
    laneLabel: "UK Relabel Lane",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-500",
    textColor: "text-blue-700",
    badgeBg: "bg-blue-100",
    badgeText: "text-blue-800",
    badgeBorder: "border-blue-200",
    dotColor: "bg-blue-500",
    dotGlow: "shadow-blue-400/60",
    productBg: "bg-white",
    productBorder: "border-blue-200"
  },
  EU_RELABEL: {
    label: "EU Relabel",
    laneLabel: "EU Relabel Lane",
    bgColor: "bg-green-50",
    borderColor: "border-green-500",
    textColor: "text-green-700",
    badgeBg: "bg-green-100",
    badgeText: "text-green-800",
    badgeBorder: "border-green-200",
    dotColor: "bg-green-500",
    dotGlow: "shadow-green-400/60",
    productBg: "bg-white",
    productBorder: "border-green-200"
  },
  UK_STANDARD: {
    label: "UK Standard",
    laneLabel: "UK Standard Lane",
    bgColor: "bg-gray-50",
    borderColor: "border-gray-400",
    textColor: "text-gray-700",
    badgeBg: "bg-gray-100",
    badgeText: "text-gray-800",
    badgeBorder: "border-gray-200",
    dotColor: "bg-gray-400",
    dotGlow: "shadow-gray-400/60",
    productBg: "bg-white",
    productBorder: "border-gray-200"
  },
  EU_STANDARD: {
    label: "EU Standard",
    laneLabel: "EU Direct Lane",
    bgColor: "bg-red-50",
    borderColor: "border-red-500",
    textColor: "text-red-700",
    badgeBg: "bg-red-100",
    badgeText: "text-red-800",
    badgeBorder: "border-red-200",
    dotColor: "bg-red-500",
    dotGlow: "shadow-red-400/60",
    productBg: "bg-white",
    productBorder: "border-red-200"
  }
} as const;

export type RoutingTypeKey = keyof typeof ROUTING_TYPE_CONFIG;

export const QUANTITY_CHANGE_REASON_LABEL: Record<RelabelQuantityChangeReason, string> = {
  DAMAGED: "Damaged",
  FAILED_QC: "Failed QC",
  SHORT_DELIVERY: "Short delivery",
  INCORRECT_PRODUCT: "Incorrect product",
  OTHER: "Other"
};

export const QUANTITY_CHANGE_REASON_OPTIONS: { value: RelabelQuantityChangeReason; label: string }[] = (
  Object.keys(QUANTITY_CHANGE_REASON_LABEL) as RelabelQuantityChangeReason[]
).map((value) => ({ value, label: QUANTITY_CHANGE_REASON_LABEL[value] }));
